import { InboxPage } from '@/pages/Inbox';
import { PATIENT_LIST_ORG_STORAGE_KEY } from '@/utils/organizationFilterStorage';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useLocalStorage } from 'usehooks-ts';
import { z } from 'zod';

const InboxSearchSchema = z.object({
  organizationId: z.string().min(1).optional(),
  pageIndex: z.number().int().min(0).default(0),
  /** The conversation open in the chat sidebar, and the patient it belongs to. Both live in the URL so
   *  the open thread survives a reload and can be shared; a message-line click already knows both. */
  conversation: z.string().min(1).optional(),
  conversationPatientId: z.string().min(1).optional(),
});

const InboxRoute = (): React.ReactNode => {
  // oxlint-disable-next-line no-use-before-define
  const { organizationId: urlOrganizationId, pageIndex, conversation, conversationPatientId } = Route.useSearch();
  // oxlint-disable-next-line no-use-before-define
  const navigate = useNavigate({ from: Route.fullPath });
  const [storedOrganizationId, setStoredOrganizationId] = useLocalStorage<string | undefined>(
    PATIENT_LIST_ORG_STORAGE_KEY,
    undefined,
  );

  // URL param wins (bookmarks/shared links); fall back to the sticky stored value.
  const organizationId = urlOrganizationId ?? storedOrganizationId;

  return (
    <InboxPage
      organizationId={organizationId}
      setOrganizationId={(organizationId) => {
        setStoredOrganizationId(organizationId);
        void navigate({ search: (prev) => ({ ...prev, organizationId, pageIndex: 0 }) });
      }}
      pageIndex={pageIndex}
      setPageIndex={(pageIndex) => {
        void navigate({ search: (prev) => ({ ...prev, pageIndex }), replace: true });
      }}
      conversationId={conversation}
      conversationPatientId={conversationPatientId}
      // Replaced rather than pushed: a practitioner clicking down a queue of threads shouldn't have to
      // walk back through every one of them to leave the inbox.
      openConversation={(conversationPatientId, conversation) => {
        void navigate({ search: (prev) => ({ ...prev, conversation, conversationPatientId }), replace: true });
      }}
      closeConversation={() => {
        void navigate({
          search: (prev) => ({ ...prev, conversation: undefined, conversationPatientId: undefined }),
          replace: true,
        });
      }}
    />
  );
};

export const Route = createFileRoute('/_auth/inbox/')({
  component: InboxRoute,
  validateSearch: InboxSearchSchema,
  loader: () => ({ crumb: 'Inbox' }),
});
